import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useBeforeunload } from 'app/hooks/useBeforeunload'

/**
 * Asks before leaving a page with unsaved changes: closing or reloading the tab, following an in-app
 * link (sidebar, rail, breadcrumbs), or the page's own buttons through the returned navigate.
 *
 * The app mounts BrowserRouter rather than a data router, so useBlocker is unavailable and the
 * browser's Back button is not covered. Links are caught in the capture phase instead, before
 * React Router's own click handler sees them.
 */
export function useUnsavedChangesGuard(dirty: boolean, what = `your changes`) {
  const navigate = useNavigate()
  const [confirm] = useConfirmation()
  const dirtyRef = useRef(dirty)
  useEffect(() => {
    dirtyRef.current = dirty
  }, [dirty])

  const confirmLeave = () =>
    confirm({
      title: `Leave without saving?`,
      description: `If you leave now, ${what} will be lost.`,
      variant: `danger`,
      submitLabel: `Leave`,
      cancelLabel: `Keep editing`,
    }).then(
      () => true,
      () => false,
    )

  useBeforeunload((event) => {
    if (dirtyRef.current) event.preventDefault()
  })

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!dirtyRef.current || event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = (event.target as Element | null)?.closest?.(`a[href]`)
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === `_blank`) return
      const url = new URL(anchor.href)
      if (url.origin !== window.location.origin) return
      const to = `${url.pathname}${url.search}${url.hash}`
      if (to === `${window.location.pathname}${window.location.search}${window.location.hash}`) return
      event.preventDefault()
      event.stopPropagation()
      confirmLeave().then((leave) => leave && navigate(to))
    }
    document.addEventListener(`click`, onClick, true)
    return () => document.removeEventListener(`click`, onClick, true)
  }, [])

  return async (to: string) => {
    if (dirtyRef.current && !(await confirmLeave())) return
    navigate(to)
  }
}
